import { useEffect, useState } from 'react'
import { useAppStore } from './useAppStore'
import { getTimeContext } from '../../utils/timeContext'
/**
 * useStreakInDangerNudge — App-Feel §16.C
 *
 * Returns whether the learner should see a "your streak is at risk"-nudge,
 * plus a `dismiss` callback that hides it for the rest of the day.
 *
 * Conditions (all must hold):
 *  - learner has an investment to protect — `currentTotalStreak >= 3`
 *  - learner has not practiced today — `dailyStreak.lastActiveDate !== today`
 *  - it is late enough that "still today" feels urgent — hour >= 17
 *  - learner has not already dismissed the nudge today
 *
 * The nudge is intentionally cosmetic: it does NOT block anything, it does
 * NOT show a pop-up. The consumer (StreakFlame via the `inDanger` prop)
 * adds a soft pulse-ring and an aria-label so the learner understands.
 *
 * One-shot per day via sessionStorage (not localStorage — we want it to
 * re-appear if the tab is reopened, which is a fresh consideration moment).
 */
export function useStreakInDangerNudge(): {
  inDanger: boolean
  dismiss: () => void
} {
  const { state } = useAppStore()
  const streak = state?.streak?.currentTotalStreak ?? 0
  const lastActiveDate = state?.dailyStreak?.lastActiveDate ?? ''
  // Re-evaluate every minute so the nudge can appear at 17:00 without a
  // remount. Cheap — the dependency array makes React only re-run when
  // the minute boundary actually crosses.
  const [minuteTick, setMinuteTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setMinuteTick((n) => n + 1), 60_000)
    return () => clearInterval(id)
  }, [])
  const [dismissedKey, setDismissedKey] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem('mp:streak-nudge:dismissed') ?? null
    } catch {
      return null
    }
  })
  const today = new Date().toISOString().slice(0, 10)
  // Re-read the time context (uses the shared time-of-day helper so all
  // hour-based features agree on "what time is it").
  const { hour } = getTimeContext()
  void minuteTick // referenced so the effect's tick keeps `hour` fresh
  const conditionsMet =
    streak >= 3 &&
    lastActiveDate !== today &&
    hour >= 17 &&
    dismissedKey !== today
  const dismiss = () => {
    try {
      sessionStorage.setItem('mp:streak-nudge:dismissed', today)
    } catch {
      // Ignore — at worst the nudge reappears once
    }
    setDismissedKey(today)
  }
  return {
    inDanger: conditionsMet,
    dismiss,
  }
}
